import * as Sentry from "@sentry/react";

// Helper function to set user context
export const setSentryUser = (user: {
  _id: string;
  email: string;
  orgId: string;
}) => {
  Sentry.setUser({
    id: user._id,
    email: user.email,
    orgId: user.orgId,
  });
};

// Collaboration breadcrumb. Breadcrumbs are NOT events, so they bypass the
// error/fatal level filter in beforeSend (sentry.ts) and are simply dropped if
// no error is ever captured in the session. When a collab error IS captured,
// these give the trail of what happened (autosave → publish → sync state) right
// before it — exactly what was missing while diagnosing the "edits revert to
// older version" reports.
export const addCollabBreadcrumb = (
  message: string,
  data?: Record<string, unknown>,
) => {
  Sentry.addBreadcrumb({
    category: "collaboration",
    level: "info",
    message,
    data,
  });
  // Breadcrumbs only surface in Sentry; mirror them locally so a dev testing
  // two browsers can see which autosave branch fired.
  if (import.meta.env.DEV) {
    console.debug("[COLLAB]", message, data ?? "");
  }
};

export const captureSentryError = (
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: "warning" | "error" | "fatal";
    fingerprint?: string[];
  },
) => {
  Sentry.captureException(error, (scope) => {
    if (context?.tags) {
      for (const [key, value] of Object.entries(context.tags)) {
        scope.setTag(key, value);
      }
    }
    if (context?.extra) {
      for (const [key, value] of Object.entries(context.extra)) {
        scope.setExtra(key, value);
      }
    }
    if (context?.level) {
      scope.setLevel(context.level);
    }
    if (context?.fingerprint) {
      scope.setFingerprint(context.fingerprint);
    }
    return scope;
  });
};
